import React, { useState, useEffect, useRef } from 'react';
import { db, auth } from '../../firebase/config';
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { Search, Printer, User, BookOpen, GraduationCap } from 'lucide-react';
import { getStudentPhotoUrl } from '../../utils/mediaUrls';

function AttendanceReportWidget() {
  const reportRef = useRef();
  
  const [currentUser, setCurrentUser] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [searchRollNumber, setSearchRollNumber] = useState('');
  const [searchName, setSearchName] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  
  const [student, setStudent] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLayout, setSelectedLayout] = useState('layout2');
  const [error, setError] = useState('');

  // Listen to Firebase Auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('Firebase Auth State Changed:', user);
      setAuthUser(user);
      
      if (user) {
        // For anonymous auth, get roll number from localStorage
        if (user.isAnonymous) {
          console.log('Anonymous user detected');
          loadStudentFromLocalStorage();
        } else {
          // For staff/non-anonymous users, get by email
          loadStaffUser(user.email);
        }
      } else {
        setError('Please log in to view attendance');
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Load attendance data when student is selected
  useEffect(() => {
    if (selectedStudent) {
      loadAttendanceData(selectedStudent);
    }
  }, [selectedStudent]);

  const loadStudentFromLocalStorage = () => {
    try {
      let rollNumber = null;

      // Strategy 1: Check all possible direct localStorage keys
      const possibleKeys = [
        'rollNumber', 'roll_number', 'studentRollNumber', 'studentId',
        'roll', 'rollNo', 'roll_no', 'studentRoll', 'student_roll_number',
        'admissionNumber', 'admission_number', 'registrationNumber'
      ];
      
      for (const key of possibleKeys) {
        const value = localStorage.getItem(key);
        if (value && value.trim()) {
          rollNumber = value.trim();
          console.log(`Found roll number in localStorage key: ${key} = ${rollNumber}`);
          break;
        }
      }

      // Strategy 2: Check JSON objects in localStorage
      if (!rollNumber) {
        const possibleObjectKeys = [
          'student', 'studentData', 'user', 'userData', 
          'currentUser', 'loggedInUser', 'authUser',
          'studentInfo', 'userInfo', 'profile'
        ];

        for (const key of possibleObjectKeys) {
          const dataStr = localStorage.getItem(key);
          if (dataStr) {
            try {
              const data = JSON.parse(dataStr);
              // Try multiple property names
              rollNumber = data.rollNumber || data.roll_number || data.studentId ||
                          data.roll || data.rollNo || data.roll_no || 
                          data.studentRollNumber || data.student_roll_number ||
                          data.admissionNumber || data.admission_number ||
                          data.registrationNumber || data.id;
              
              if (rollNumber) {
                console.log(`Found roll number in localStorage object: ${key}.rollNumber = ${rollNumber}`);
                break;
              }
            } catch (e) {
              // Not valid JSON, skip
            }
          }
        }
      }

      // Strategy 3: Try to get from URL if present (for direct links)
      if (!rollNumber) {
        const urlParams = new URLSearchParams(window.location.search);
        rollNumber = urlParams.get('rollNumber') || urlParams.get('roll') || urlParams.get('studentId');
        if (rollNumber) {
          console.log(`Found roll number in URL: ${rollNumber}`);
        }
      }

      // Strategy 4: Check sessionStorage as well (including studentSession)
      if (!rollNumber) {
        // First check for studentSession specifically (your login stores it here)
        const studentSession = sessionStorage.getItem('studentSession');
        if (studentSession) {
          try {
            const sessionData = JSON.parse(studentSession);
            rollNumber = sessionData.rollNumber;
            if (rollNumber) {
              console.log(`Found roll number in sessionStorage.studentSession: ${rollNumber}`);
            }
          } catch (e) {
            console.error('Error parsing studentSession:', e);
          }
        }

        // Then check other sessionStorage keys
        if (!rollNumber) {
          for (const key of possibleKeys) {
            const value = sessionStorage.getItem(key);
            if (value && value.trim()) {
              rollNumber = value.trim();
              console.log(`Found roll number in sessionStorage: ${key} = ${rollNumber}`);
              break;
            }
          }
        }
      }

      console.log('Final roll number detected:', rollNumber);

      if (rollNumber) {
        // Set as student user
        setCurrentUser({ role: 'student', rollNumber: rollNumber });
        setSelectedStudent(rollNumber);
      } else {
        // Show all localStorage contents for debugging
        console.log('=== All localStorage contents ===');
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          console.log(`${key}:`, localStorage.getItem(key));
        }
        console.log('=================================');
        
        setError('Roll number not found in browser storage. Please log in again.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading student from localStorage:', error);
      setError('Error loading student data');
      setLoading(false);
    }
  };

  const loadStaffUser = async (userEmail) => {
    try {
      console.log('Loading staff user for:', userEmail);

      // Fetch user details from users collection
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', userEmail));
      const userSnapshot = await getDocs(q);

      if (!userSnapshot.empty) {
        const userData = userSnapshot.docs[0].data();
        console.log('Found staff user:', userData);
        setCurrentUser(userData);
        setLoading(false);
      } else {
        setError(`Staff user not found in database for email: ${userEmail}`);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading staff user:', error);
      setError(`Error loading staff data: ${error.message}`);
      setLoading(false);
    }
  };

  const loadAttendanceData = async (rollNumber) => {
    setLoading(true);
    setError('');
    try {
      const studentDoc = await getDoc(doc(db, 'students', rollNumber));
      if (studentDoc.exists()) {
        setStudent(studentDoc.data());
      } else {
        setError('Student not found');
        setLoading(false);
        return;
      }

      const attendanceDoc = await getDoc(doc(db, 'attendance', rollNumber));
      if (attendanceDoc.exists()) {
        setAttendance(attendanceDoc.data());
      } else {
        setError('Attendance data not found');
        setLoading(false);
        return;
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Error loading attendance data');
      setLoading(false);
    }
  };

  const handleSearchByRollNumber = () => {
    if (searchRollNumber.trim()) {
      setSelectedStudent(searchRollNumber.trim());
      setSearchResults([]);
      setSearchName('');
    }
  };

  const handleSearchByName = async () => {
    if (!searchName.trim()) return;
    
    try {
      setLoading(true);
      const studentsRef = collection(db, 'students');
      const studentsSnapshot = await getDocs(studentsRef);
      
      const results = [];
      studentsSnapshot.forEach((doc) => {
        const data = doc.data();
        const fullName = `${data.firstName} ${data.surname}`.toLowerCase();
        if (fullName.includes(searchName.toLowerCase())) {
          results.push({
            rollNumber: doc.id,
            name: `${data.firstName} ${data.surname}`,
            division: data.division,
            batch: data.batch
          });
        }
      });
      
      setSearchResults(results);
      setLoading(false);
    } catch (error) {
      console.error('Error searching students:', error);
      setError('Error searching students');
      setLoading(false);
    }
  };

  const handleSelectStudent = (rollNumber) => {
    setSelectedStudent(rollNumber);
    setSearchResults([]);
    setSearchName('');
    setSearchRollNumber('');
  };

  const handlePrint = () => window.print();

  // Separate theory and practical courses
  const theoryCourses = attendance?.courses?.filter(course => 
    course.name.toLowerCase().includes('theory') && 
    !course.name.toLowerCase().includes('tutorial') &&
    !course.name.toLowerCase().includes('lab') &&
    !course.name.toLowerCase().includes('practical')
  ) || [];

  // Add UHV to theory
  const uhvCourse = attendance?.courses?.find(course => 
    course.name.toLowerCase().includes('universal human values')
  );
  if (uhvCourse && !theoryCourses.includes(uhvCourse)) {
    theoryCourses.push(uhvCourse);
  }

  const practicalCourses = attendance?.courses?.filter(course => 
    course.name.toLowerCase().includes('practical') || 
    course.name.toLowerCase().includes('lab') ||
    course.name.toLowerCase().includes('tutorial') ||
    course.name.toLowerCase().includes('workshop')
  ) || [];

  const allCourses = attendance?.courses || [];

  // Render different layouts
  const renderLayout = () => {
    switch(selectedLayout) {
      case 'layout1':
        return <Layout1 courses={allCourses} attendance={attendance} />;
      case 'layout2':
        return <Layout2 theoryCourses={theoryCourses} practicalCourses={practicalCourses} attendance={attendance} />;
      case 'layout3':
        return <Layout3 courses={allCourses} attendance={attendance} />;
      case 'layout4':
        return <Layout4 theoryCourses={theoryCourses} practicalCourses={practicalCourses} attendance={attendance} />;
      default:
        return <Layout2 theoryCourses={theoryCourses} practicalCourses={practicalCourses} attendance={attendance} />;
    }
  };

  // Show loading spinner
  if (loading && !currentUser) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error if any
  if (error && !student) {
    // Get all localStorage items for display
    const storageItems = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const value = localStorage.getItem(key);
      // Try to detect if it might be the roll number
      const looksLikeRollNumber = /^\d{6,10}$/.test(value) || /^[A-Z]{2}\d{3,6}$/.test(value);
      storageItems.push({ key, value: value?.substring(0, 50), looksLikeRollNumber, source: 'localStorage' });
    }

    // Also check sessionStorage
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      const value = sessionStorage.getItem(key);
      const looksLikeRollNumber = /^\d{6,10}$/.test(value) || /^[A-Z]{2}\d{3,6}$/.test(value);
      storageItems.push({ key, value: value?.substring(0, 50), looksLikeRollNumber, source: 'sessionStorage' });
    }

    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-8">
          <p className="text-red-600 text-lg mb-4">{error}</p>
          
          {authUser && authUser.isAnonymous && (
            <div className="mt-4 bg-blue-50 border border-blue-200 p-4 rounded-lg text-sm text-left max-w-2xl mx-auto">
              <p className="font-semibold text-gray-800 mb-3">🔍 Automatic Detection - Browser Storage Contents:</p>
              
              <div className="bg-white p-3 rounded border border-gray-300 text-xs overflow-auto max-h-60">
                {storageItems.length > 0 ? (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-2">Source</th>
                        <th className="text-left py-2 px-2">Key</th>
                        <th className="text-left py-2 px-2">Value (first 50 chars)</th>
                        <th className="text-center py-2 px-2">Looks like Roll No?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {storageItems.map((item, idx) => (
                        <tr key={idx} className={`border-b ${item.looksLikeRollNumber ? 'bg-green-50' : ''}`}>
                          <td className="py-1 px-2 text-gray-600">{item.source}</td>
                          <td className="py-1 px-2 font-mono text-blue-600">{item.key}</td>
                          <td className="py-1 px-2 font-mono text-gray-700">{item.value || '(empty)'}</td>
                          <td className="py-1 px-2 text-center">
                            {item.looksLikeRollNumber ? '✅' : '❌'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-gray-600">No items found in localStorage</p>
                )}
              </div>

              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-300 rounded">
                <p className="text-sm text-gray-700">
                  <strong>💡 What the widget is looking for:</strong>
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  The widget automatically checks these patterns:
                </p>
                <ul className="text-xs text-gray-600 mt-1 list-disc list-inside">
                  <li>Direct keys: rollNumber, roll_number, studentId, roll, rollNo, etc.</li>
                  <li>Inside objects: student.rollNumber, userData.roll_number, <strong>studentSession.rollNumber</strong>, etc.</li>
                  <li>URL parameters: ?rollNumber=... or ?roll=...</li>
                  <li>Both localStorage AND sessionStorage</li>
                </ul>
              </div>

              <div className="mt-3 p-3 bg-gray-100 rounded">
                <p className="text-xs text-gray-700">
                  <strong>📋 Check browser console (F12)</strong> for detailed detection logs
                </p>
              </div>

              {/* Manual Override Option */}
              <div className="mt-4 p-4 bg-white border-2 border-blue-300 rounded-lg">
                <p className="font-semibold text-gray-800 mb-2">🔧 Quick Fix: Enter Roll Number Manually</p>
                <p className="text-xs text-gray-600 mb-3">
                  If the widget can't detect your roll number, enter it here to view your attendance:
                </p>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    placeholder="Enter your roll number"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm"
                    id="manualRollNumberInput"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        setSelectedStudent(e.target.value.trim());
                        setError('');
                        setLoading(true);
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      const input = document.getElementById('manualRollNumberInput');
                      if (input && input.value.trim()) {
                        setSelectedStudent(input.value.trim());
                        setError('');
                        setLoading(true);
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-semibold"
                  >
                    View
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {authUser && !authUser.isAnonymous && (
            <div className="mt-4 bg-blue-50 border border-blue-200 p-4 rounded-lg text-sm text-left max-w-md mx-auto">
              <p className="font-semibold text-gray-800 mb-2">Logged in as (Staff):</p>
              <p className="text-gray-700">{authUser.email}</p>
              <p className="text-xs text-gray-600 mt-2">
                Make sure this email exists in your Firestore 'users' collection.
              </p>
            </div>
          )}
          
          {!authUser && (
            <div className="mt-4 bg-yellow-50 border border-yellow-300 p-4 rounded-lg text-sm max-w-md mx-auto">
              <p className="text-gray-700">
                No active Firebase Auth session detected. Please log in first.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show search interface for staff roles
  if (currentUser && currentUser.role !== 'student' && !selectedStudent) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center mb-6">
          <GraduationCap className="w-8 h-8 text-blue-600 mr-3" />
          <h2 className="text-2xl font-bold text-gray-800">Student Attendance Report</h2>
        </div>

        <div className="space-y-6">
          {/* Search by Roll Number */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Search by Roll Number
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={searchRollNumber}
                onChange={(e) => setSearchRollNumber(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearchByRollNumber()}
                placeholder="Enter roll number (e.g., 2024001)"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                onClick={handleSearchByRollNumber}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
              >
                <Search className="w-4 h-4" />
                <span>Search</span>
              </button>
            </div>
          </div>

          {/* OR Divider */}
          <div className="flex items-center">
            <div className="flex-1 border-t border-gray-300"></div>
            <span className="px-4 text-gray-500 font-semibold">OR</span>
            <div className="flex-1 border-t border-gray-300"></div>
          </div>

          {/* Search by Name */}
          <div className="bg-green-50 p-4 rounded-lg">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Search by Name
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearchByName()}
                placeholder="Enter student name"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <button
                onClick={handleSearchByName}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
              >
                <Search className="w-4 h-4" />
                <span>Search</span>
              </button>
            </div>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Search Results</h3>
              <div className="space-y-2">
                {searchResults.map((result) => (
                  <div
                    key={result.rollNumber}
                    onClick={() => handleSelectStudent(result.rollNumber)}
                    className="bg-white p-3 rounded-lg border border-gray-200 hover:border-blue-500 cursor-pointer transition-all hover:shadow-md"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-800">{result.name}</p>
                        <p className="text-sm text-gray-600">
                          Roll: {result.rollNumber} | Division: {result.division} | Batch: {result.batch}
                        </p>
                      </div>
                      <User className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              <strong>Note:</strong> Enter a student's roll number or search by name to view their attendance report.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show loading for attendance data
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading attendance data...</p>
        </div>
      </div>
    );
  }

  // Show error if data not found
  if (!student || !attendance) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-8">
          <p className="text-xl text-gray-600">Attendance data not found</p>
          {currentUser?.role !== 'student' && (
            <button
              onClick={() => {
                setSelectedStudent(null);
                setStudent(null);
                setAttendance(null);
              }}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Search Another Student
            </button>
          )}
        </div>
      </div>
    );
  }

  // Render the attendance report
  return (
    <div className="bg-gray-100 print:bg-white">
      <div className="max-w-[210mm] mx-auto print:px-0">
        
        {/* Action Buttons */}
        <div className="flex justify-between items-center mb-4 print:hidden">
          <div className="flex items-center space-x-3">
            {currentUser?.role !== 'student' && (
              <button
                onClick={() => {
                  setSelectedStudent(null);
                  setStudent(null);
                  setAttendance(null);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center space-x-2"
              >
                <Search className="w-4 h-4" />
                <span>Search Another</span>
              </button>
            )}
          </div>

          {/* Layout Selector */}
          <div className="flex items-center space-x-3">
            <label className="text-sm font-semibold text-gray-700">Layout:</label>
            <select 
              value={selectedLayout}
              onChange={(e) => setSelectedLayout(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="layout1">Layout 1 - Simple Linear</option>
              <option value="layout2">Layout 2 - Grouped Sections ⭐</option>
              <option value="layout3">Layout 3 - Compact</option>
              <option value="layout4">Layout 4 - Modern Cards</option>
            </select>
          </div>

          <button 
            onClick={handlePrint} 
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Printer className="w-4 h-4" />
            <span>Print</span>
          </button>
        </div>

        {/* A4 Report */}
        <div 
          ref={reportRef}
          className="bg-white shadow-lg print:shadow-none"
          style={{
            width: '210mm',
            minHeight: '297mm',
            padding: '12mm',
            margin: '0 auto'
          }}
        >
          {/* Letterhead */}
          <div className="mb-3">
            <img 
              src="/ybit-letterhead.png" 
              alt="YBIT Letterhead" 
              className="w-full h-auto object-contain"
              style={{ maxHeight: '80px' }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
          
          <div className="border-b-2 border-black mb-3"></div>

          {/* Report Title */}
          <h2 className="text-lg font-bold text-center mb-4 uppercase">Attendance Report - Semester I</h2>

          {/* Student Info with Photo */}
          <div className="grid grid-cols-4 gap-4 mb-4 pb-3 border-b border-gray-300">
            <div className="col-span-3">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div>
                  <span className="font-semibold">Name:</span> {student.firstName} {student.surname}
                </div>
                <div>
                  <span className="font-semibold">Roll Number:</span> {selectedStudent}
                </div>
                <div>
                  <span className="font-semibold">Division:</span> {student.division}
                </div>
                <div>
                  <span className="font-semibold">Batch:</span> {student.batch}
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              {getStudentPhotoUrl(student) ? (
                <img 
                  src={getStudentPhotoUrl(student)} 
                  alt="Student" 
                  className="w-24 h-24 object-cover rounded border-2 border-gray-300"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/150?text=No+Photo';
                  }}
                />
              ) : (
                <div className="w-24 h-24 bg-gray-200 rounded border-2 border-gray-300 flex items-center justify-center">
                  <User className="w-12 h-12 text-gray-400" />
                </div>
              )}
            </div>
          </div>

          {/* Render Selected Layout */}
          {renderLayout()}
        </div>
      </div>
    </div>
  );
}

// Layout 1: Simple Linear Format
function Layout1({ courses, attendance }) {
  return (
    <div className="mb-4">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-blue-600 text-white">
            <th className="border border-gray-300 px-2 py-2 text-left">Subject Name</th>
            <th className="border border-gray-300 px-2 py-2 text-center w-24">Conducted</th>
            <th className="border border-gray-300 px-2 py-2 text-center w-24">Attended</th>
            <th className="border border-gray-300 px-2 py-2 text-center w-24">Percentage</th>
          </tr>
        </thead>
        <tbody>
          {courses.map((course, index) => {
            const percentage = course.conducted > 0 
              ? ((course.attended / course.conducted) * 100).toFixed(2) 
              : 0;
            return (
              <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                <td className="border border-gray-300 px-2 py-1.5">{course.name}</td>
                <td className="border border-gray-300 px-2 py-1.5 text-center">{course.conducted}</td>
                <td className="border border-gray-300 px-2 py-1.5 text-center font-semibold">{course.attended}</td>
                <td className="border border-gray-300 px-2 py-1.5 text-center">{percentage}%</td>
              </tr>
            );
          })}
          <tr className="bg-blue-100 font-bold">
            <td className="border border-gray-300 px-2 py-2">THEORY TOTAL</td>
            <td className="border border-gray-300 px-2 py-2 text-center">
              {attendance.theoryTotal?.conducted || 0}
            </td>
            <td className="border border-gray-300 px-2 py-2 text-center">
              {attendance.theoryTotal?.attended || 0}
            </td>
            <td className="border border-gray-300 px-2 py-2 text-center">
              {Number(attendance.theoryPercentage).toFixed(2)}%
            </td>
          </tr>
          <tr className="bg-green-100 font-bold">
            <td className="border border-gray-300 px-2 py-2">PRACTICAL TOTAL</td>
            <td className="border border-gray-300 px-2 py-2 text-center">
              {attendance.practicalTotal?.conducted || 0}
            </td>
            <td className="border border-gray-300 px-2 py-2 text-center">
              {attendance.practicalTotal?.attended || 0}
            </td>
            <td className="border border-gray-300 px-2 py-2 text-center">
              {Number(attendance.practicalPercentage).toFixed(2)}%
            </td>
          </tr>
          <tr className="bg-blue-600 text-white font-bold">
            <td className="border border-gray-300 px-2 py-2">OVERALL TOTAL</td>
            <td className="border border-gray-300 px-2 py-2 text-center">
              {(attendance.theoryTotal?.conducted || 0) + (attendance.practicalTotal?.conducted || 0)}
            </td>
            <td className="border border-gray-300 px-2 py-2 text-center">
              {(attendance.theoryTotal?.attended || 0) + (attendance.practicalTotal?.attended || 0)}
            </td>
            <td className="border border-gray-300 px-2 py-2 text-center">
              {Number(attendance.overallPercentage).toFixed(2)}%
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// Layout 2: Grouped by Theory/Practical
function Layout2({ theoryCourses, practicalCourses, attendance }) {
  return (
    <div className="mb-4 space-y-4">
      {/* Theory Section */}
      <div>
        <div className="bg-blue-600 text-white px-3 py-2 rounded-t font-bold flex items-center">
          <BookOpen className="w-4 h-4 mr-2" />
          THEORY SUBJECTS
        </div>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-200">
              <th className="border border-gray-300 px-2 py-1.5 text-left">Subject Name</th>
              <th className="border border-gray-300 px-2 py-1.5 text-center w-20">Conducted</th>
              <th className="border border-gray-300 px-2 py-1.5 text-center w-20">Attended</th>
              <th className="border border-gray-300 px-2 py-1.5 text-center w-20">%</th>
            </tr>
          </thead>
          <tbody>
            {theoryCourses.map((course, index) => {
              const percentage = course.conducted > 0 
                ? ((course.attended / course.conducted) * 100).toFixed(2) 
                : 0;
              return (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="border border-gray-300 px-2 py-1">{course.name}</td>
                  <td className="border border-gray-300 px-2 py-1 text-center">{course.conducted}</td>
                  <td className="border border-gray-300 px-2 py-1 text-center font-semibold">{course.attended}</td>
                  <td className="border border-gray-300 px-2 py-1 text-center">{percentage}%</td>
                </tr>
              );
            })}
            <tr className="bg-blue-100 font-bold">
              <td className="border border-gray-300 px-2 py-1.5">THEORY TOTAL</td>
              <td className="border border-gray-300 px-2 py-1.5 text-center">
                {attendance.theoryTotal?.conducted || 0}
              </td>
              <td className="border border-gray-300 px-2 py-1.5 text-center">
                {attendance.theoryTotal?.attended || 0}
              </td>
              <td className="border border-gray-300 px-2 py-1.5 text-center">
                {Number(attendance.theoryPercentage).toFixed(2)}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Practical Section */}
      <div>
        <div className="bg-green-600 text-white px-3 py-2 rounded-t font-bold flex items-center">
          <GraduationCap className="w-4 h-4 mr-2" />
          PRACTICAL SUBJECTS
        </div>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-200">
              <th className="border border-gray-300 px-2 py-1.5 text-left">Subject Name</th>
              <th className="border border-gray-300 px-2 py-1.5 text-center w-20">Conducted</th>
              <th className="border border-gray-300 px-2 py-1.5 text-center w-20">Attended</th>
              <th className="border border-gray-300 px-2 py-1.5 text-center w-20">%</th>
            </tr>
          </thead>
          <tbody>
            {practicalCourses.map((course, index) => {
              const percentage = course.conducted > 0 
                ? ((course.attended / course.conducted) * 100).toFixed(2) 
                : 0;
              return (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="border border-gray-300 px-2 py-1">{course.name}</td>
                  <td className="border border-gray-300 px-2 py-1 text-center">{course.conducted}</td>
                  <td className="border border-gray-300 px-2 py-1 text-center font-semibold">{course.attended}</td>
                  <td className="border border-gray-300 px-2 py-1 text-center">{percentage}%</td>
                </tr>
              );
            })}
            <tr className="bg-green-100 font-bold">
              <td className="border border-gray-300 px-2 py-1.5">PRACTICAL TOTAL</td>
              <td className="border border-gray-300 px-2 py-1.5 text-center">
                {attendance.practicalTotal?.conducted || 0}
              </td>
              <td className="border border-gray-300 px-2 py-1.5 text-center">
                {attendance.practicalTotal?.attended || 0}
              </td>
              <td className="border border-gray-300 px-2 py-1.5 text-center">
                {Number(attendance.practicalPercentage).toFixed(2)}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Overall Summary */}
      <div className="bg-blue-600 text-white px-3 py-2 rounded font-bold text-center">
        OVERALL ATTENDANCE PERCENTAGE: {Number(attendance.overallPercentage).toFixed(2)}%
      </div>
    </div>
  );
}

// Layout 3: Compact with Subject Codes
function Layout3({ courses, attendance }) {
  const getSubjectCode = (name) => {
    if (name.includes('Applied Mathematics')) return 'AM-I';
    if (name.includes('Applied Physics')) return 'AP';
    if (name.includes('Applied Chemistry')) return 'AC';
    if (name.includes('Engineering Mechanics')) return 'EM';
    if (name.includes('Basic Electrical')) return 'BEE';
    if (name.includes('Professional Communication')) return 'PCE';
    if (name.includes('C Programming')) return 'CP';
    if (name.includes('Universal Human')) return 'UHV';
    if (name.includes('Engineering Workshop')) return 'EW-1';
    return 'N/A';
  };

  const getType = (name) => {
    if (name.includes('Theory')) return '(T)';
    if (name.includes('Tutorial')) return '(Tut)';
    if (name.includes('Practical')) return '(P)';
    if (name.includes('Lab')) return '(L)';
    return '';
  };

  return (
    <div className="mb-4">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="bg-blue-600 text-white">
            <th className="border border-gray-300 px-2 py-1.5 text-left w-20">Code</th>
            <th className="border border-gray-300 px-2 py-1.5 text-left">Subject Name</th>
            <th className="border border-gray-300 px-2 py-1.5 text-center w-16">Cond</th>
            <th className="border border-gray-300 px-2 py-1.5 text-center w-16">Att</th>
            <th className="border border-gray-300 px-2 py-1.5 text-center w-16">%</th>
          </tr>
        </thead>
        <tbody>
          {courses.map((course, index) => {
            const percentage = course.conducted > 0 ? ((course.attended / course.conducted) * 100).toFixed(0) : 0;
            return (
              <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                <td className="border border-gray-300 px-2 py-1">
                  {getSubjectCode(course.name)} {getType(course.name)}
                </td>
                <td className="border border-gray-300 px-2 py-1">{course.name}</td>
                <td className="border border-gray-300 px-2 py-1 text-center">{course.conducted}</td>
                <td className="border border-gray-300 px-2 py-1 text-center font-semibold">{course.attended}</td>
                <td className="border border-gray-300 px-2 py-1 text-center">{percentage}%</td>
              </tr>
            );
          })}
          <tr className="bg-blue-100 font-bold">
            <td className="border border-gray-300 px-2 py-1.5" colSpan="2">THEORY TOTAL</td>
            <td className="border border-gray-300 px-2 py-1.5 text-center">{attendance.theoryTotal?.conducted || 0}</td>
            <td className="border border-gray-300 px-2 py-1.5 text-center">{attendance.theoryTotal?.attended || 0}</td>
            <td className="border border-gray-300 px-2 py-1.5 text-center">{Number(attendance.theoryPercentage).toFixed(0)}%</td>
          </tr>
          <tr className="bg-green-100 font-bold">
            <td className="border border-gray-300 px-2 py-1.5" colSpan="2">PRACTICAL TOTAL</td>
            <td className="border border-gray-300 px-2 py-1.5 text-center">{attendance.practicalTotal?.conducted || 0}</td>
            <td className="border border-gray-300 px-2 py-1.5 text-center">{attendance.practicalTotal?.attended || 0}</td>
            <td className="border border-gray-300 px-2 py-1.5 text-center">{Number(attendance.practicalPercentage).toFixed(0)}%</td>
          </tr>
          <tr className="bg-blue-600 text-white font-bold">
            <td className="border border-gray-300 px-2 py-2" colSpan="2">OVERALL TOTAL</td>
            <td className="border border-gray-300 px-2 py-2 text-center">
              {(attendance.theoryTotal?.conducted || 0) + (attendance.practicalTotal?.conducted || 0)}
            </td>
            <td className="border border-gray-300 px-2 py-2 text-center">
              {(attendance.theoryTotal?.attended || 0) + (attendance.practicalTotal?.attended || 0)}
            </td>
            <td className="border border-gray-300 px-2 py-2 text-center">{Number(attendance.overallPercentage).toFixed(0)}%</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// Layout 4: Modern Card Style
function Layout4({ theoryCourses, practicalCourses, attendance }) {
  return (
    <div className="mb-4 space-y-4">
      {/* Theory Card */}
      <div className="border-2 border-blue-600 rounded-lg overflow-hidden">
        <div className="bg-blue-600 text-white px-3 py-2 flex justify-between items-center">
          <span className="font-bold flex items-center">
            <BookOpen className="w-4 h-4 mr-2" />
            THEORY ATTENDANCE
          </span>
          <span className="text-xl font-bold">{Number(attendance.theoryPercentage).toFixed(2)}%</span>
        </div>
        <div className="p-3 bg-blue-50 text-sm">
          <p>
            <strong>Conducted:</strong> {attendance.theoryTotal?.conducted || 0} | 
            <strong> Attended:</strong> {attendance.theoryTotal?.attended || 0} | 
            <strong> Missed:</strong> {(attendance.theoryTotal?.conducted || 0) - (attendance.theoryTotal?.attended || 0)}
          </p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-200">
            <tr>
              <th className="border border-gray-300 px-2 py-1 text-left">Subject</th>
              <th className="border border-gray-300 px-2 py-1 text-center w-20">Cond</th>
              <th className="border border-gray-300 px-2 py-1 text-center w-20">Att</th>
              <th className="border border-gray-300 px-2 py-1 text-center w-12">✓</th>
            </tr>
          </thead>
          <tbody>
            {theoryCourses.map((course, index) => (
              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="border border-gray-300 px-2 py-1">{course.name}</td>
                <td className="border border-gray-300 px-2 py-1 text-center">{course.conducted}</td>
                <td className="border border-gray-300 px-2 py-1 text-center font-semibold">{course.attended}</td>
                <td className="border border-gray-300 px-2 py-1 text-center">
                  {course.attended === course.conducted ? '✓' : '⚠'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Practical Card */}
      <div className="border-2 border-green-600 rounded-lg overflow-hidden">
        <div className="bg-green-600 text-white px-3 py-2 flex justify-between items-center">
          <span className="font-bold flex items-center">
            <GraduationCap className="w-4 h-4 mr-2" />
            PRACTICAL ATTENDANCE
          </span>
          <span className="text-xl font-bold">{Number(attendance.practicalPercentage).toFixed(2)}%</span>
        </div>
        <div className="p-3 bg-green-50 text-sm">
          <p>
            <strong>Conducted:</strong> {attendance.practicalTotal?.conducted || 0} | 
            <strong> Attended:</strong> {attendance.practicalTotal?.attended || 0} | 
            <strong> Missed:</strong> {(attendance.practicalTotal?.conducted || 0) - (attendance.practicalTotal?.attended || 0)}
          </p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-200">
            <tr>
              <th className="border border-gray-300 px-2 py-1 text-left">Subject</th>
              <th className="border border-gray-300 px-2 py-1 text-center w-20">Cond</th>
              <th className="border border-gray-300 px-2 py-1 text-center w-20">Att</th>
              <th className="border border-gray-300 px-2 py-1 text-center w-12">✓</th>
            </tr>
          </thead>
          <tbody>
            {practicalCourses.map((course, index) => (
              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="border border-gray-300 px-2 py-1">{course.name}</td>
                <td className="border border-gray-300 px-2 py-1 text-center">{course.conducted}</td>
                <td className="border border-gray-300 px-2 py-1 text-center font-semibold">{course.attended}</td>
                <td className="border border-gray-300 px-2 py-1 text-center">
                  {course.attended === course.conducted ? '✓' : '⚠'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Overall Summary */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-3 rounded-lg text-center">
        <p className="text-2xl font-bold">🎯 OVERALL ATTENDANCE: {Number(attendance.overallPercentage).toFixed(2)}%</p>
        <p className="text-sm mt-1">
          ({(attendance.theoryTotal?.attended || 0) + (attendance.practicalTotal?.attended || 0)} / 
          {(attendance.theoryTotal?.conducted || 0) + (attendance.practicalTotal?.conducted || 0)})
        </p>
      </div>
    </div>
  );
}

export default AttendanceReportWidget;
